import type { DrawingMode } from './types';

interface MapDrawingModeCoordinatorOptions {
	hasSelection: () => boolean;
	resetDrawing: () => void;
	clearSelection: () => void;
	whenSelectionSyncIdle: () => Promise<void>;
	toggleDrawingMode: (mode: DrawingMode) => void;
}

interface MapDrawingResetGuardOptions {
	hasDrawingSelection: () => boolean;
	resetDrawing: () => void;
}

interface MapDragEndGuardOptions {
	onDragEnd: () => void;
	onConfirmedDragEnd: () => void;
}

/**
 * Always forwards dragend so map bounds can stay in sync, but only confirms
 * side effects that require a matching dragstart (such as clearing a drawing).
 */
export function createMapDragEndGuard({
	onDragEnd,
	onConfirmedDragEnd
}: MapDragEndGuardOptions) {
	let hasDragStarted = false;

	function markDragStarted(): void {
		hasDragStarted = true;
	}

	function handleDragEnd(): void {
		const isConfirmedMapDrag = hasDragStarted;
		hasDragStarted = false;

		try {
			if (isConfirmedMapDrag) {
				onConfirmedDragEnd();
			}
		} finally {
			onDragEnd();
		}
	}

	return { markDragStarted, handleDragEnd };
}

/**
 * Keeps the completed shape attached to a selection created by drawing while
 * still allowing unrelated cluster refreshes to clear stale overlays.
 */
export function createMapDrawingResetGuard({
	hasDrawingSelection,
	resetDrawing
}: MapDrawingResetGuardOptions): () => void {
	return () => {
		if (hasDrawingSelection()) return;
		resetDrawing();
	};
}

/**
 * Coordinates replacing a completed map selection with a new drawing.
 * The next tool is armed only after the old H3 route sync settles, otherwise
 * that delayed route transition would immediately reset the new drawing mode.
 */
export function createMapDrawingModeCoordinator({
	hasSelection,
	resetDrawing,
	clearSelection,
	whenSelectionSyncIdle,
	toggleDrawingMode
}: MapDrawingModeCoordinatorOptions) {
	let latestToggleIntent = 0;
	let pendingSelectionClear: Promise<void> | null = null;
	let isDisposed = false;

	function startSelectionClear(): void {
		resetDrawing();
		clearSelection();

		const pending = whenSelectionSyncIdle().finally(() => {
			if (pendingSelectionClear === pending) {
				pendingSelectionClear = null;
			}
		});
		pendingSelectionClear = pending;
	}

	async function toggle(mode: DrawingMode): Promise<void> {
		const intent = ++latestToggleIntent;

		if (hasSelection() && !pendingSelectionClear) {
			startSelectionClear();
		}

		if (pendingSelectionClear) {
			await pendingSelectionClear;
		}

		if (isDisposed || intent !== latestToggleIntent) return;
		toggleDrawingMode(mode);
	}

	function dispose(): void {
		isDisposed = true;
		latestToggleIntent += 1;
	}

	return { toggle, dispose };
}
